// The schedule on record for a saved view — read from the BACKEND, not a local
// cache. A schedule created by any teammate (or in another browser / session)
// for this workspace + website + view must show up in the Share drawer with its
// real details, and stopping it must be visible everywhere at once.
import { useCallback } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import {
	ScheduledReports,
	periodToInterval,
	type ScheduledReportDTO
} from 'services/user'
import type { ScheduleCfg } from '../store/use-report-layout-store'

// The same key the Scheduled Reports page's list query uses, so a schedule saved
// or stopped in the drawer refreshes that page (and vice versa) through ONE cache
// entry instead of two views of the truth drifting apart.
export const scheduledReportsKey = (accountId?: string, assetId?: string) => [
	'scheduled-reports',
	accountId,
	assetId
]

// Wire enums → the drawer's display labels (its INTERVAL / RANGE option lists).
const INTERVAL_LABEL: Record<string, string> = {
	DAILY: 'Daily',
	WEEKLY: 'Weekly',
	MONTHLY: 'Monthly',
	QUARTERLY: 'Quarterly'
}
const RANGE_LABEL: Record<string, string> = {
	LAST_7: 'Last 7 days',
	LAST_30: 'Last 30 days',
	THIS_MONTH: 'This month',
	LAST_MONTH: 'Last month',
	THIS_QUARTER: 'This quarter',
	LAST_QUARTER: 'Last quarter'
}

/** A backend schedule row as the drawer's form record. */
export const scheduleFromDto = (dto: ScheduledReportDTO): ScheduleCfg => ({
	interval: INTERVAL_LABEL[periodToInterval(dto.period)] ?? 'Weekly',
	// The drawer sends a plain yyyy-mm-dd, which the API stores as UTC midnight —
	// read it back in UTC so a west-of-Greenwich viewer doesn't see the day before.
	startDate: moment.utc(dto.starting_date).format('YYYY-MM-DD'),
	range: RANGE_LABEL[dto.analytics_date_range] ?? 'Last 30 days',
	name: dto.label ?? 'Analytics report',
	adAccounts: dto.ad_account_ids ?? [],
	campaigns: dto.campaign_ids ?? [],
	recipients: dto.recipients ?? [],
	reportId: dto.id
})

export const useViewSchedule = (viewId: string | null) => {
	const { accountId, assetId } = useParams<{
		accountId: string
		assetId: string
	}>()
	const enabled = !!accountId && !!assetId
	const query = useQuery(
		scheduledReportsKey(accountId, assetId),
		() => ScheduledReports.list(accountId as string, assetId as string),
		{ enabled }
	)
	const queryClient = useQueryClient()
	// After create / update / stop: re-read the list so the drawer (and the
	// Scheduled Reports page) reflect the row the backend actually holds.
	const refresh = useCallback(
		() =>
			queryClient.invalidateQueries(scheduledReportsKey(accountId, assetId)),
		[queryClient, accountId, assetId]
	)
	// The list comes back sorted by next run, so the first row bound to this view
	// is the schedule the drawer manages.
	const dto = viewId
		? query.data?.find((r) => r.view_id === viewId) ?? null
		: null
	return {
		schedule: dto ? scheduleFromDto(dto) : null,
		isLoading: enabled && query.isLoading,
		isError: query.isError,
		refresh
	}
}
